import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import rcsLogo from "../../assets/RCS-Logo-Blue.png";
import CheckVoucherPreview from "../../components/accounting/CheckVoucherPreview";
import { downloadCheckVoucherAttachmentsZip } from "../../utils/checkVoucherAttachmentsZip";
import { API_BASE_URL, authFetch } from "../../config/api";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getDisplayName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.user_name ||
  "";

const getCheckNumberStatus = (voucher) => {
  const hasCheckNo = Boolean(String(voucher?.check_no || "").trim());

  return hasCheckNo
    ? {
        label: "With check no.",
        className: "bg-green-100 text-green-700 border border-green-200",
      }
    : {
        label: "Missing check no.",
        className: "bg-amber-100 text-amber-700 border border-amber-200",
    };
};

const getAssignmentStatus = (voucher, currentUserId) => {
  const assignedUserId = voucher?.assigned_checker_user_id;

  if (!assignedUserId) {
    return {
      label: "Unassigned",
      className: "bg-slate-100 text-slate-700 border border-slate-200",
    };
  }

  if (String(assignedUserId) === String(currentUserId)) {
    return {
      label: "Assigned to me",
      className: "bg-blue-100 text-blue-700 border border-blue-200",
    };
  }

  return {
    label: "Assigned to another",
    className: "bg-amber-100 text-amber-700 border border-amber-200",
  };
};

const TAX_OPTIONS = {
  input_tax: { label: "Input tax", rate: 12 },
  ewt_1: { label: "EWT 1%", rate: 1 },
  ewt_2: { label: "EWT 2%", rate: 2 },
  ewt_5: { label: "EWT 5%", rate: 5 },
};

const getTaxDisplay = (taxType, taxRate) => {
  const option = TAX_OPTIONS[taxType];
  if (!option) return "—";

  const rate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : option.rate;
  return `${option.label}${rate ? ` (${rate}%)` : ""}`;
};

const renderRequestAttachments = (requests = []) => {
  const requestsWithAttachments = (requests || []).filter(
    (request) => Array.isArray(request.attachments) && request.attachments.length > 0
  );

  if (!requestsWithAttachments.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-4">
        <p className="font-semibold text-gray-800">Attached documents</p>
        <p className="mt-1 text-sm text-gray-600">
          No attached documents were uploaded for the linked requests.
        </p>
      </div>
    );
  }

  const totalFiles = requestsWithAttachments.reduce(
    (sum, request) => sum + (request.attachments?.length || 0),
    0
  );

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p className="font-semibold text-gray-800">Attached documents</p>
          <p className="text-xs text-gray-500">
            Files uploaded with the linked requests. Review them before checking the voucher.
          </p>
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
          {totalFiles} file{totalFiles !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="space-y-3">
        {requestsWithAttachments.map((request) => (
          <div key={request.id} className="space-y-2">
            <div className="text-xs font-semibold text-blue">
              {request.rfp_no || "Request"} ({request.attachments.length} file
              {request.attachments.length !== 1 ? "s" : ""})
            </div>
            <div className="space-y-2">
              {request.attachments.map((doc, docIndex) => (
                <a
                  key={doc.id || docIndex}
                  href={doc.file_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-blue-50 transition"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-800">{doc.file_name || "Attachment"}</p>
                    <p className="text-xs text-gray-500">
                      {(doc.file_type || "file").toString()} •{" "}
                      {doc.created_at ? new Date(doc.created_at).toLocaleString() : "Uploaded"}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-blue">Open</span>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  const contentType = res.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return JSON.parse(raw);
  }

  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const normalizeParticulars = (particulars) => {
  if (Array.isArray(particulars)) return particulars;
  if (particulars && typeof particulars === "object") return [particulars];
  return [];
};

const formatRequestParticulars = (request) =>
  normalizeParticulars(request?.particulars)
    .map((particular) => particular.description || particular.name || "")
    .filter(Boolean)
    .join(", ");

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const enrichVoucherRequestsWithAttachments = async (voucherData) => {
  const requests = Array.isArray(voucherData?.requests) ? voucherData.requests : [];
  if (!requests.length) return voucherData;

  try {
    const res = await authFetch(`${API_BASE_URL}/api/accounting/requests`, {
      credentials: "include",
    });
    const json = await readJsonResponse(res);
    if (!json.success || !Array.isArray(json.data)) {
      return voucherData;
    }

    const requestMap = new Map(json.data.map((request) => [String(request.id), request]));
    const enrichedRequests = requests.map((request) => {
      const fallbackRequest = requestMap.get(String(request.id));
      const fallbackAttachments = Array.isArray(fallbackRequest?.attachments) ? fallbackRequest.attachments : [];
      const currentAttachments = Array.isArray(request.attachments) ? request.attachments : [];

      return {
        ...(fallbackRequest || {}),
        ...request,
        mode_of_payment: request.mode_of_payment || fallbackRequest?.mode_of_payment || "",
        attachments: currentAttachments.length ? currentAttachments : fallbackAttachments,
      };
    });

    return {
      ...voucherData,
      requests: enrichedRequests,
    };
  } catch (err) {
    console.error("Failed to enrich voucher requests with attachments", err);
    return voucherData;
  }
};

const buildPrintHtml = ({
  voucherNo,
  voucherDate,
  payee,
  checkNo,
  checkDate,
  amount,
  amountWords,
  taxType,
  taxRate,
  taxAmount,
  lines,
  requests,
  preparerName,
  preparerSignatureUrl,
  checkedByName,
  checkedBySignatureUrl,
  approvedByName,
}) => {
  const primaryRequest = Array.isArray(requests) && requests.length ? requests[0] : null;
  const shipmentConsignee =
    primaryRequest?.shipment_consignee || primaryRequest?.receiver_name || "";
  const attachmentBlocks = (requests || [])
    .filter((request) => Array.isArray(request.attachments) && request.attachments.length > 0)
    .map(
      (request) => `
        <div style="margin-bottom:14px;">
          <div style="font-size:13px;font-weight:700;color:#1f2937;margin-bottom:8px;">
            ${request.rfp_no || "Request"} (${request.attachments.length} file${request.attachments.length !== 1 ? "s" : ""})
          </div>
          <div style="display:flex;flex-direction:column;gap:8px;">
            ${(request.attachments || [])
              .map(
                (doc) => `
                  <a href="${doc.file_url || "#"}" target="_blank" rel="noreferrer" style="text-decoration:none;color:inherit;">
                    <div style="border:1px solid #d1d5db;border-radius:14px;padding:10px 12px;background:#fff;display:flex;justify-content:space-between;gap:12px;align-items:center;">
                      <div style="min-width:0;flex:1;">
                        <div style="font-size:13px;font-weight:700;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                          ${doc.file_name || "Attachment"}
                        </div>
                        <div style="font-size:11px;color:#6b7280;">
                          ${(doc.file_type || "file").toString()} • ${
                            doc.created_at ? new Date(doc.created_at).toLocaleString() : "Uploaded"
                          }
                        </div>
                      </div>
                      <div style="font-size:11px;font-weight:700;color:#2563eb;white-space:nowrap;">Open</div>
                    </div>
                  </a>`
              )
              .join("")}
          </div>
        </div>`
    )
    .join("");

  const lineRows = (lines || [])
    .map(
      (line) => `<tr>
        <td style="padding:8px 10px;border:1px solid #000;">
          <div>${line.account_name || ""}</div>
          ${line.particulars ? `<div style="font-size:11px;color:#444;margin-top:2px;">${line.particulars}</div>` : ""}
        </td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.debit || 0) ? money(line.debit) : ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.credit || 0) ? money(line.credit) : ""}</td>
      </tr>`
    )
    .join("");

  const requestRows = (requests || [])
    .map(
      (r, index) => `<tr>
        <td style="padding:8px 10px;border:1px solid #000;">${index + 1}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${r.rfp_no || ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${r.job_order || ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${r.payee || ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${r.mode_of_payment || "—"}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">₱${money(r.total_amount || 0)}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${formatRequestParticulars(r) || "—"}</td>
      </tr>`
    )
    .join("");

  return `
    <html>
      <head>
        <title>${voucherNo || "Check Voucher"}</title>
        <style>
          *{box-sizing:border-box;}
          body{font-family:"Times New Roman",serif;color:#000;margin:18px;font-size:12px;}
          .brand{display:flex;justify-content:center;align-items:center;margin-bottom:8px;}
          .brand img{max-width:160px;max-height:60px;object-fit:contain;}
          h1{text-align:center;letter-spacing:2px;margin:0 0 4px;font-size:16px;}
          .subhead{text-align:center;font-weight:600;margin:0 0 10px;font-size:12px;}
          .meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px 16px;margin-bottom:10px;font-size:12px;}
          .meta div{padding:2px 0;}
          table{width:100%;border-collapse:collapse;margin-top:6px;font-size:11px;}
          th,td{vertical-align:top;}
          .sig-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-top:16px;}
          .sig{text-align:center;min-height:80px;display:flex;flex-direction:column;justify-content:flex-end;gap:4px;}
          .sig img{max-height:40px;object-fit:contain;margin:0 auto;}
          .sig-name{font-size:11px;min-height:14px;}
          .line{border-top:1px solid #000;height:1px;margin:4px 0;}
          @page{size:portrait;margin:12mm;}
        </style>
      </head>
      <body>
        <div class="brand">
          <img src="${rcsLogo}" alt="RCS Logistics Philippines logo">
        </div>
        <h1>CHECK VOUCHER</h1>
        <div class="subhead">${voucherNo || ""}</div>

        <div class="meta">
          <div><strong>Date:</strong> ${voucherDate || ""}</div>
          <div><strong>Payee:</strong> ${payee || ""}</div>
          <div><strong>Check No.:</strong> ${checkNo || ""}</div>
          <div><strong>Check Date:</strong> ${checkDate || ""}</div>
          <div><strong>Due Date:</strong> ${primaryRequest?.due_date ? new Date(primaryRequest.due_date).toLocaleDateString() : "—"}</div>
          <div><strong>Mode of Payment:</strong> ${primaryRequest?.mode_of_payment || "—"}</div>
          ${
            primaryRequest?.job_order && shipmentConsignee
              ? `<div><strong>Shipment Consignee:</strong> ${shipmentConsignee}</div>`
              : ""
          }
          <div><strong>Amount:</strong> ₱${money(amount || 0)}</div>
          <div><strong>Amount in Words:</strong> ${amountWords || ""} Pesos Only</div>
        </div>

        <div style="margin:12px 0;">
          <div style="font-weight:700;margin-bottom:6px;font-size:12px;">The above check is in payment of the following</div>
          <table>
            <thead>
              <tr>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">#</th>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">RFP No</th>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Job Order</th>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Payee</th>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Due Date</th>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Mode of Payment</th>
                <th style="text-align:right;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Amount</th>
                <th style="text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Particulars</th>
              </tr>
            </thead>
            <tbody>
              ${requestRows}
            </tbody>
          </table>
        </div>

        ${
          attachmentBlocks
            ? `
              <div style="margin:12px 0 14px;">
                <div style="font-weight:700;margin-bottom:6px;font-size:12px;">Attached documents</div>
                <div style="border:1px solid #d1d5db;border-radius:16px;background:#f9fafb;padding:14px 16px;">
                  <div style="font-size:12px;color:#6b7280;margin-bottom:10px;">Files uploaded with the linked requests. Review them before checking the voucher.</div>
                  <div style="display:flex;flex-direction:column;gap:12px;">
                    ${attachmentBlocks}
                  </div>
                </div>
              </div>
            `
            : ""
        }

        <div style="margin:12px 0;">
          <div style="font-weight:700;margin-bottom:6px;font-size:12px;">Accounting Entries</div>
          <table>
            <thead>
              <tr>
                <th style="width:60%;text-align:left;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Account</th>
                <th style="width:20%;text-align:right;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Debit</th>
                <th style="width:20%;text-align:right;border:1px solid #000;padding:6px 8px;background:#f5f5f5;">Credit</th>
              </tr>
            </thead>
            <tbody>
              ${lineRows}
              <tr>
                <td style="padding:8px 10px;border:1px solid #000;font-weight:700;text-align:right;">TOTAL</td>
                <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(amount || 0)}</td>
                <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(amount || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style="margin-top:12px;font-size:11px;">
          <div><strong>Prepared by:</strong> ${preparerName || "____________________"}</div>
          <div><strong>Checked by:</strong> ____________________</div>
          <div><strong>Approved by:</strong> ${approvedByName || "____________________"}</div>
          <div><strong>Received by:</strong> ____________________</div>
        </div>

        <div class="sig-grid">
          <div class="sig">
            ${preparerSignatureUrl ? `<img src="${preparerSignatureUrl}" alt="Preparer signature">` : "<div style='height:40px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${preparerName || "Name"}</div>
            <div style="font-size:11px;">Prepared By:</div>
          </div>
          <div class="sig">
            ${checkedBySignatureUrl ? `<img src="${checkedBySignatureUrl}" alt="Checked by signature">` : "<div style='height:40px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${checkedByName || "Name"}</div>
            <div style="font-size:11px;">Checked By:</div>
          </div>
          <div class="sig">
            <div style='height:40px'></div>
            <div class="line"></div>
            <div class="sig-name">${approvedByName || "Name"}</div>
            <div style="font-size:11px;">Approved By:</div>
          </div>
          <div class="sig">
            <div style='height:40px'></div>
            <div class="line"></div>
            <div class="sig-name">Name</div>
            <div style="font-size:11px;">Received By:</div>
          </div>
        </div>
      </body>
    </html>
  `;
};

const PendingVoucherCheck = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isAllowed =
    user?.role === "Superadmin" && user?.department?.toLowerCase() === "accounting";
  const currentUserId = user?.id || user?.user_id || "";

  const [pendingVouchers, setPendingVouchers] = useState([]);
  const [checkedByUsers, setCheckedByUsers] = useState([]);
  const [selectedVoucherId, setSelectedVoucherId] = useState("");
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [previewShipment, setPreviewShipment] = useState(null);
  const [checkedByUserId, setCheckedByUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const currentCheckedByUser = useMemo(
    () => checkedByUsers.find((voucher) => String(voucher.user_id) === String(checkedByUserId)),
    [checkedByUsers, checkedByUserId]
  );

  const checkedByDisplayName = currentCheckedByUser ? getDisplayName(currentCheckedByUser) : "";

  const selectedVoucherTotals = useMemo(() => {
    const requests = Array.isArray(selectedVoucher?.requests) ? selectedVoucher.requests : [];
    const lines = Array.isArray(selectedVoucher?.lines) ? selectedVoucher.lines : [];

    return {
      requestTotal: requests.reduce((sum, request) => sum + Number(request.total_amount || 0), 0),
      lineDebitTotal: lines.reduce((sum, line) => sum + Number(line.debit || 0), 0),
      lineCreditTotal: lines.reduce((sum, line) => sum + Number(line.credit || 0), 0),
    };
  }, [selectedVoucher]);

  useEffect(() => {
    const loadCheckedByUsers = async () => {
      try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/checked-by-users`, {
          credentials: "include",
        });
        const json = await readJsonResponse(res);
        if (json.success) {
          setCheckedByUsers(json.data || []);
        }
      } catch (err) {
        console.error("load checked-by users", err);
      }
    };
    loadCheckedByUsers();
  }, []);

  const loadPending = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/pending`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to load pending vouchers");
      const vouchers = await Promise.all(
        (json.data || []).map(async (voucher) => {
          try {
            const detailRes = await authFetch(
              `${API_BASE_URL}/api/accounting/check-vouchers/pending/${voucher.id}`,
              { credentials: "include" }
            );
            const detailJson = await readJsonResponse(detailRes);
            if (!detailJson.success) {
              throw new Error(detailJson.message || `Unable to load voucher ${voucher.id}`);
            }
            const enrichedVoucher = await enrichVoucherRequestsWithAttachments(detailJson.data || voucher);
            return enrichedVoucher || detailJson.data || voucher;
          } catch (detailError) {
            console.error("Failed to hydrate pending voucher queue item", voucher?.id, detailError);
            const enrichedVoucher = await enrichVoucherRequestsWithAttachments(voucher);
            return enrichedVoucher || voucher;
          }
        })
      );
      setPendingVouchers(vouchers);
      setSelectedVoucherId((currentId) => {
        if (currentId && vouchers.some((voucher) => String(voucher.id) === String(currentId))) {
          return currentId;
        }
        return vouchers[0]?.id || "";
      });
    } catch (err) {
      setError(err.message || "Unable to load pending vouchers");
    } finally {
      setLoading(false);
    }
  };

  const loadVoucherDetail = async (voucherId) => {
    if (!voucherId) {
      setSelectedVoucher(null);
      setPreviewShipment(null);
      setCheckedByUserId("");
      return;
    }
    setDetailLoading(true);
    setError("");
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/check-vouchers/pending/${voucherId}`,
        { credentials: "include" }
      );
      const json = await readJsonResponse(res);
      console.log("📦 Voucher API Response:", json);
      console.log("📎 Requests data:", json.data?.requests);
      if (json.data?.requests) {
        json.data.requests.forEach((req, idx) => {
          console.log(`Request ${idx} (${req.rfp_no}):`, {
            has_attachments: !!req.attachments,
            attachments_count: req.attachments?.length || 0,
            attachments_data: req.attachments
          });
        });
      }
      if (!json.success) throw new Error(json.message || "Unable to load voucher");
      const enrichedData = await enrichVoucherRequestsWithAttachments(json.data || null);
      setSelectedVoucher(enrichedData || null);
      setCheckedByUserId(json.data?.assigned_checker_user_id ? String(json.data.assigned_checker_user_id) : "");
    } catch (err) {
      setSelectedVoucher(null);
      setPreviewShipment(null);
      setError(err.message || "Unable to load voucher");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const fetchShipment = async () => {
      const jobOrder = selectedVoucher?.requests?.[0]?.job_order || selectedVoucher?.job_order;
      if (!jobOrder) {
        setPreviewShipment(null);
        return;
      }

      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
        url.searchParams.set("q", jobOrder);
        url.searchParams.set("limit", 1);
        const res = await authFetch(url, { credentials: "include" });
        const json = await res.json();
        if (json.success && json.data?.length) {
          setPreviewShipment(json.data[0]);
        } else {
          setPreviewShipment(null);
        }
      } catch (err) {
        console.error("shipment lookup", err);
        setPreviewShipment(null);
      }
    };

    fetchShipment();
  }, [selectedVoucher]);

  useEffect(() => {
    loadPending();
  }, []);

  useEffect(() => {
    loadVoucherDetail(selectedVoucherId);
  }, [selectedVoucherId]);

  const amountWords = selectedVoucher?.amount_words || "";

  const printVoucher = () => {
    if (!selectedVoucher) return;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.open();
    win.document.write(
      buildPrintHtml({
        voucherNo: selectedVoucher.voucher_no,
        voucherDate: selectedVoucher.voucher_date,
        payee: selectedVoucher.payee,
        checkNo: selectedVoucher.check_no || "",
        checkDate: selectedVoucher.check_date || "",
        amount: selectedVoucher.amount,
        amountWords: selectedVoucher.amount_words,
        lines: selectedVoucher.lines || [],
        requests: selectedVoucher.requests || [],
        preparerName: selectedVoucher.preparer_name,
        preparerSignatureUrl: selectedVoucher.preparer_signature_url,
        checkedByName: selectedVoucher.assigned_checker_name || checkedByDisplayName,
        checkedBySignatureUrl: null,
        approvedByName: selectedVoucher.approved_by_name || "EDR/ERI/AGR",
        taxType: selectedVoucher.tax_type,
        taxRate: selectedVoucher.tax_rate,
        taxAmount: selectedVoucher.tax_amount,
      })
    );
    win.document.close();
    win.focus();
    win.print();
  };

  const downloadAttachmentsZip = async () => {
    if (!selectedVoucher) return;
    setZipLoading(true);
    setError("");
    try {
      await downloadCheckVoucherAttachmentsZip({
        voucher: selectedVoucher,
        voucherNo: selectedVoucher.voucher_no,
        requests: selectedVoucher.requests || [],
        lines: selectedVoucher.lines || [],
      });
    } catch (err) {
      setError(err.message || "Unable to download attachments ZIP");
    } finally {
      setZipLoading(false);
    }
  };

  const exportPendingToExcel = async () => {
    if (!pendingVouchers.length) {
      setError("No pending vouchers to export.");
      return;
    }

    setExporting(true);
    setError("");

    try {
      const detailRows = await Promise.all(
        pendingVouchers.map(async (voucher) => {
          const res = await authFetch(
            `${API_BASE_URL}/api/accounting/check-vouchers/pending/${voucher.id}`,
            { credentials: "include" }
          );
          const json = await readJsonResponse(res);
          if (!json.success) {
            throw new Error(json.message || `Unable to load voucher ${voucher.voucher_no || voucher.id}`);
          }
          const enrichedVoucher = await enrichVoucherRequestsWithAttachments(json.data || null);
          return enrichedVoucher || json.data || voucher;
        })
      );

      const voucherSummaryRows = detailRows.map((voucher) => {
        const requests = Array.isArray(voucher.requests) ? voucher.requests : [];
        const lines = Array.isArray(voucher.lines) ? voucher.lines : [];
        const requestTotal = requests.reduce((sum, request) => sum + Number(request.total_amount || 0), 0);
        const lineDebitTotal = lines.reduce((sum, line) => sum + Number(line.debit || 0), 0);
        const lineCreditTotal = lines.reduce((sum, line) => sum + Number(line.credit || 0), 0);

      return {
          "Voucher No": voucher.voucher_no || "",
          "Voucher Date": formatDate(voucher.voucher_date || voucher.created_at),
          "Created At": formatDateTime(voucher.created_at),
          "Updated At": formatDateTime(voucher.updated_at),
          Payee: voucher.payee || "",
          "Check No.": voucher.check_no || "",
          "Check Date": formatDate(voucher.check_date),
          "RFP Nos": requests.map((request) => request.rfp_no).filter(Boolean).join(", "),
          "Job Orders": requests.map((request) => request.job_order).filter(Boolean).join(", "),
          "Assigned Checker": voucher.assigned_checker_name || "",
          "Prepared By": voucher.preparer_name || "",
          "Approved By": voucher.approved_by_name || "",
          "Tax Treatment": getTaxDisplay(voucher.tax_type, voucher.tax_rate),
          "Amount Words": voucher.amount_words || "",
          "Voucher Amount": Number(voucher.amount || 0),
          "Request Total": requestTotal,
          "Tax Amount": Number(voucher.tax_amount || 0),
          "Debit Total": lineDebitTotal,
          "Credit Total": lineCreditTotal,
          Status: voucher.checked_by_user_id ? "Checked" : "Pending",
        };
      });

      const requestRows = detailRows.flatMap((voucher) =>
        (voucher.requests || []).map((request) => ({
          "Voucher No": voucher.voucher_no || "",
          "Voucher Date": formatDate(voucher.voucher_date || voucher.created_at),
          "RFP No": request.rfp_no || "",
          "Job Order": request.job_order || "",
          Payee: request.payee || "",
          "Request Amount": Number(request.total_amount || 0),
          "Attached Files": Array.isArray(request.attachments) ? request.attachments.length : 0,
          Particulars: formatRequestParticulars(request) || "",
        }))
      );

      const lineRows = detailRows.flatMap((voucher) =>
        (voucher.lines || []).map((line) => ({
          "Voucher No": voucher.voucher_no || "",
          "Voucher Date": formatDate(voucher.voucher_date || voucher.created_at),
          Account: line.account_name || "",
          Particulars: line.particulars || "",
          Debit: Number(line.debit || 0),
          Credit: Number(line.credit || 0),
        }))
      );

      const makeSheet = (rows, totalColumns = []) => {
        const sheetRows = rows.map((row) => ({ ...row }));
        const totalsRow = {};
        totalColumns.forEach((column) => {
          totalsRow[column] = rows.reduce((sum, row) => sum + Number(row[column] || 0), 0);
        });
        if (totalColumns.length) {
          const firstColumn = Object.keys(rows[0] || {})[0] || "Total";
          totalsRow[firstColumn] = "TOTAL";
          sheetRows.push(totalsRow);
        }

        return XLSX.utils.json_to_sheet(sheetRows);
      };

      const workbook = XLSX.utils.book_new();
      const summarySheet = makeSheet(voucherSummaryRows, [
        "Voucher Amount",
        "Request Total",
        "Tax Amount",
        "Debit Total",
        "Credit Total",
      ]);
      const requestsSheet = makeSheet(requestRows, ["Request Amount", "Attached Files"]);
      const linesSheet = makeSheet(lineRows, ["Debit", "Credit"]);

      XLSX.utils.book_append_sheet(workbook, summarySheet, "Pending Vouchers");
      XLSX.utils.book_append_sheet(workbook, requestsSheet, "Voucher Requests");
      XLSX.utils.book_append_sheet(workbook, linesSheet, "Voucher Lines");

      const summaryRange = XLSX.utils.decode_range(summarySheet["!ref"]);
      const requestsRange = XLSX.utils.decode_range(requestsSheet["!ref"]);
      const linesRange = XLSX.utils.decode_range(linesSheet["!ref"]);
      summarySheet["!autofilter"] = { ref: XLSX.utils.encode_range(summaryRange) };
      requestsSheet["!autofilter"] = { ref: XLSX.utils.encode_range(requestsRange) };
      linesSheet["!autofilter"] = { ref: XLSX.utils.encode_range(linesRange) };

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `pending-vouchers-${dateStamp}.xlsx`);
      setMessage("Pending vouchers exported to Excel.");
    } catch (err) {
      setError(err.message || "Unable to export pending vouchers");
    } finally {
      setExporting(false);
    }
  };

  const handleAssignChecker = async () => {
    if (!selectedVoucher) {
      setError("Select a voucher first.");
      return;
    }
    if (!checkedByUserId) {
      setError("Select a checker.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/check-vouchers/${selectedVoucher.id}/checked-by`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ assigned_checker_user_id: checkedByUserId }),
        }
      );
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to assign checker");
      setMessage("Checker assigned.");
      await loadPending();
    } catch (err) {
      setError(err.message || "Unable to assign checker");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkChecked = async () => {
    if (!selectedVoucher) {
      setError("Select a voucher first.");
      return;
    }

    if (
      selectedVoucher.assigned_checker_user_id &&
      String(selectedVoucher.assigned_checker_user_id) !== String(currentUserId)
    ) {
      setError("This voucher is assigned to another checker.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/check-vouchers/${selectedVoucher.id}/checked`,
        {
          method: "PUT",
          credentials: "include",
        }
      );
      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Unable to mark voucher checked");
      setMessage("Voucher marked as checked.");
      await loadPending();
    } catch (err) {
      setError(err.message || "Unable to mark voucher checked");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = window.setTimeout(() => setMessage(""), 3000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [message]);

  if (!isAllowed) {
    return (
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
        <h1 className="text-2xl font-semibold text-blue">Pending Voucher Checks</h1>
        <p className="mt-2 text-sm text-gray-600">
          This page is available to Superadmin users in the Accounting department only.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading pending vouchers...</div>;
  }

  return (
    <div className="space-y-4 font-poppins">
      <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
          <h1 className="text-2xl font-semibold text-blue">Pending Voucher Checks</h1>
          <p className="text-sm text-gray-600">
            Assign a checker first, then mark the voucher as checked when it is completed.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportPendingToExcel}
            disabled={!pendingVouchers.length || exporting}
            className="px-4 py-2 rounded-lg border border-green-200 bg-green-50 text-sm font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
          <button
            onClick={() => navigate("/accounting/check-voucher/create")}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:border-gray-300"
          >
            New Voucher
          </button>
          <button
            onClick={loadPending}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:opacity-90"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {message && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {message}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-4">
        <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-blue">Pending Queue</h2>
            <p className="text-sm text-gray-500">{pendingVouchers.length} voucher(s) waiting</p>
          </div>
          <div className="max-h-[calc(100vh-18rem)] overflow-y-auto">
            {pendingVouchers.length ? (
              pendingVouchers.map((voucher) => (
                (() => {
                  const checkNumberStatus = getCheckNumberStatus(voucher);
                  const assignmentStatus = getAssignmentStatus(voucher, currentUserId);

                  return (
                <button
                  key={voucher.id}
                  type="button"
                  onClick={() => setSelectedVoucherId(voucher.id)}
                  className={`w-full text-left px-5 py-4 border-b border-gray-100 transition-colors ${
                    String(selectedVoucherId) === String(voucher.id)
                      ? "bg-blue/5"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-blue">{voucher.voucher_no || "—"}</div>
                      <div className="text-xs font-semibold text-gray-700 mt-0.5">
                        RFP No: {voucher.rfp_no || "—"}
                      </div>
                      <div className="text-sm text-gray-600">{voucher.payee || "—"}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Mode of Payment: {voucher.requests?.[0]?.mode_of_payment || "—"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Remarks: {voucher.requests?.[0]?.remarks || "—"}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Bank: {voucher.requests?.[0]?.bank || "—"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 text-right shrink-0">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${assignmentStatus.className}`}
                      >
                        {assignmentStatus.label}
                      </span>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${checkNumberStatus.className}`}
                      >
                        {checkNumberStatus.label}
                      </span>
                      <div className="text-sm font-semibold text-gray-800">₱{money(voucher.amount)}</div>
                      <div className="text-xs text-gray-500">
                        {voucher.voucher_date || voucher.created_at ? new Date(voucher.voucher_date || voucher.created_at).toLocaleDateString() : "—"}
                      </div>
                    </div>
                  </div>
                </button>
                  );
                })()
              ))
            ) : (
              <div className="px-5 py-8 text-sm text-gray-500">No pending vouchers.</div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-blue">Voucher Review</h2>
                <p className="text-sm text-gray-500">
                  {detailLoading ? "Loading voucher..." : selectedVoucher?.voucher_no || "Select a voucher from the queue"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={printVoucher}
                  disabled={!selectedVoucher}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Download / Print
                </button>
                <button
                  onClick={downloadAttachmentsZip}
                  disabled={!selectedVoucher || zipLoading}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {zipLoading ? "Preparing ZIP..." : "Download Packet ZIP"}
                </button>
              </div>
            </div>

            {selectedVoucher ? (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><strong>Date:</strong> {selectedVoucher.voucher_date || "—"}</div>
                  <div><strong>Payee:</strong> {selectedVoucher.payee || "—"}</div>
                  <div><strong>Check No.:</strong> {selectedVoucher.check_no || "—"}</div>
                  <div><strong>Check Date:</strong> {selectedVoucher.check_date || "—"}</div>
                  <div><strong>Mode of Payment:</strong> {selectedVoucher.requests?.[0]?.mode_of_payment || "—"}</div>
                  <div><strong>Tax Treatment:</strong> {getTaxDisplay(selectedVoucher.tax_type, selectedVoucher.tax_rate)}</div>
                  <div><strong>Amount:</strong> ₱{money(selectedVoucher.amount)}</div>
                  <div><strong>Amount in Words:</strong> {amountWords || "—"}</div>
                  <div><strong>Prepared by:</strong> {selectedVoucher.preparer_name || "—"}</div>
                  <div><strong>Assigned checker:</strong> {selectedVoucher.assigned_checker_name || "Pending"}</div>
                  <div><strong>Approved by:</strong> {selectedVoucher.approved_by_name || "EDR/ERI/AGR"}</div>
                </div>

                {renderRequestAttachments(selectedVoucher.requests)}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700">Assign checker</label>
                    <select
                      value={checkedByUserId}
                      onChange={(e) => setCheckedByUserId(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                    >
                      <option value="">Select checker</option>
                      {checkedByUsers.map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {getDisplayName(u)}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Superadmin users in the Accounting department only.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 items-end justify-end">
                    <button
                      onClick={handleAssignChecker}
                      disabled={saving || !checkedByUserId}
                      className="w-full px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Assign Checker"}
                    </button>
                    <button
                      onClick={handleMarkChecked}
                      disabled={
                        saving ||
                        !selectedVoucher ||
                        (selectedVoucher.assigned_checker_user_id &&
                          String(selectedVoucher.assigned_checker_user_id) !== String(currentUserId)) ||
                        selectedVoucher.checked_by_user_id
                      }
                      className="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                      {saving ? "Saving..." : "Mark as Checked"}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 text-sm">
                  <div><strong>Request total:</strong> ₱{money(selectedVoucherTotals.requestTotal)}</div>
                  <div><strong>Debit total:</strong> ₱{money(selectedVoucherTotals.lineDebitTotal)}</div>
                  <div><strong>Credit total:</strong> ₱{money(selectedVoucherTotals.lineCreditTotal)}</div>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                        <th className="px-3 py-2">Account</th>
                        <th className="px-3 py-2 text-right">Debit</th>
                        <th className="px-3 py-2 text-right">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {(selectedVoucher.lines || []).map((line, index) => (
                        <tr key={line.id || index}>
                          <td className="px-3 py-2">{line.account_name || "—"}</td>
                          <td className="px-3 py-2 text-right">
                            {line.debit ? `₱${money(line.debit)}` : ""}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {line.credit ? `₱${money(line.credit)}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50 font-semibold">
                      <tr>
                        <td className="px-3 py-2 text-right">Total</td>
                        <td className="px-3 py-2 text-right">₱{money(selectedVoucher.amount)}</td>
                        <td className="px-3 py-2 text-right">₱{money(selectedVoucher.amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                No voucher selected.
              </div>
            )}
          </div>

          <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-blue">Preview</h2>
              <span className="text-xs uppercase tracking-[0.12em] text-gray-500">
                {selectedVoucher ? "Printable layout" : "No voucher selected"}
              </span>
            </div>
            {selectedVoucher ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-xl font-bold text-center tracking-widest mb-1">CASH VOUCHER</h3>
                      <p className="text-center font-semibold mb-3 text-sm">
                        RFP No: {selectedVoucher.voucher_no || "—"}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div>
                        {selectedVoucher.voucher_date || selectedVoucher.created_at
                          ? new Date(selectedVoucher.voucher_date || selectedVoucher.created_at).toLocaleDateString()
                          : "—"}
                      </div>
                      <div>{selectedVoucher.check_no || "—"}</div>
                    </div>
                  </div>

                  <div className="mb-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <strong>Date:</strong>{" "}
                      {selectedVoucher.voucher_date || selectedVoucher.created_at
                        ? new Date(selectedVoucher.voucher_date || selectedVoucher.created_at).toLocaleDateString()
                        : "—"}
                    </div>
                    <div><strong>Payee:</strong> {selectedVoucher.payee || "—"}</div>
                    <div><strong>Job Order:</strong> {selectedVoucher.requests?.[0]?.job_order || "—"}</div>
                    <div><strong>Due Date:</strong> {selectedVoucher.requests?.[0]?.due_date ? new Date(selectedVoucher.requests[0].due_date).toLocaleDateString() : "—"}</div>
                    <div><strong>Mode of Payment:</strong> {selectedVoucher.requests?.[0]?.mode_of_payment || "—"}</div>
                    <div><strong>Corporate Client:</strong> {previewShipment?.corporate_client || "—"}</div>
                    <div className="sm:col-span-2">
                      <strong>Shipment Description:</strong>{" "}
                      {previewShipment?.shipment_description || "—"}
                    </div>
                  </div>

                  {renderRequestAttachments(selectedVoucher.requests)}
                </div>

                <CheckVoucherPreview
                  voucherNo={selectedVoucher.voucher_no}
                  voucherDate={selectedVoucher.voucher_date || selectedVoucher.created_at}
                  payee={selectedVoucher.payee}
                  checkNo={selectedVoucher.check_no}
                  checkDate={selectedVoucher.check_date}
                  details={[
                    {
                      label: "Tax Treatment",
                      value: getTaxDisplay(selectedVoucher.tax_type, selectedVoucher.tax_rate),
                    },
                  ]}
                  amountLabel="Amount"
                  amount={selectedVoucher.amount}
                  amountWords={amountWords || "—"}
                  preparerName={selectedVoucher.preparer_name}
                  assignedCheckerName={selectedVoucher.assigned_checker_name || "Pending"}
                  checkedByName={selectedVoucher.checked_by_name || "Pending"}
                  approvedByName={selectedVoucher.approved_by_name || "EDR/ERI/AGR"}
                  receivedByName={selectedVoucher.received_by_name || "Pending"}
                  preparerSignatureUrl={selectedVoucher.preparer_signature_url}
                  checkedBySignatureUrl={selectedVoucher.checked_by_signature_url || null}
                  lines={selectedVoucher.lines || []}
                  requests={selectedVoucher.requests || []}
                />
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                Select a voucher to preview it here.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingVoucherCheck;
